import { useCallback, useEffect, useRef, useState } from 'react';

const KEY = 'cbc:draft:sinistre';
const DEBOUNCE_MS = 600;

/* ─────────────────────────────────────────────────────────────
   Auto-sauvegarde en localStorage - un seul brouillon actif.
   Retourne la valeur initiale (si draft existant) + un setter
   qui persiste avec debounce + un flag "saving" pour l'UI.
   ───────────────────────────────────────────────────────────── */
export default function useDraftSinistre() {
  const [initial] = useState(() => {
    try {
      const raw = sessionStorage.getItem(KEY) || localStorage.getItem(KEY);
      return raw ? JSON.parse(raw) : null;
    } catch {
      return null;
    }
  });

  const [status, setStatus] = useState('idle'); // idle | saving | saved
  const timer = useRef(null);
  const lastSaved = useRef(null);

  const persist = useCallback((snapshot) => {
    if (timer.current) clearTimeout(timer.current);
    setStatus('saving');
    timer.current = setTimeout(() => {
      try {
        const payload = JSON.stringify({ ...snapshot, _savedAt: Date.now() });
        localStorage.setItem(KEY, payload);
        lastSaved.current = Date.now();
        setStatus('saved');
      } catch {
        setStatus('idle');
      }
    }, DEBOUNCE_MS);
  }, []);

  const clear = useCallback(() => {
    localStorage.removeItem(KEY);
    sessionStorage.removeItem(KEY);
    lastSaved.current = null;
    setStatus('idle');
  }, []);

  useEffect(() => () => timer.current && clearTimeout(timer.current), []);

  return { initial, status, persist, clear, lastSavedAt: lastSaved.current };
}
